import { isEqual } from 'date-fns';

type ResultType = OfferActivity | undefined;
type ApiResponseType = ApiSchema['OfferCollectionResponse'];
type ParamsType = ApiSchema['OfferCollectionRequest'];

/**
 * Loads the final offer for an activity for a specific date range and
 * real list of passengers.
 *
 * An activity can return multiple offers for the same date range, differing only by
 * starting point and/or ticket option. Those are matched by name, because the option
 * id is not stable across time slots.
 */
export default async function apiLoadFinalOfferActivity(
    activityId: string,
    dateFrom: Date | undefined,
    dateUntil: Date | undefined,
    passengers: Passenger[],
    promotion?: Promotion,
    listingId?: string,
    startingPoint?: string,
    option?: string,
): Promise<ApiResult<ResultType>> {
    try {
        return await callApiPost<ResultType, ApiResponseType, ParamsType>(
            '/offers',

            // Request body
            {
                offerSearchCriteria: {
                    validFrom: formatDateTimeISO(dateFrom) || undefined,
                    activityIds: [
                        activityId,
                    ],
                },
                anonymousPassengerSpecifications: getFinalPassengerSpecifications(passengers),
                // only use general promo code here, otherwise send via passengerSpec
                ...(promotion && promotion.promotionType === 'GENERAL' && {
                    promotionCodes: [promotion],
                }),
                ...(listingId && { listingId }),
            },

            // Transform result
            (response: ApiResponseType) => {
                const offer = response.offers?.find((o) => {
                    const offerPart = o.activityOfferParts?.[0];
                    const validFrom = zurichDate(offerPart?.validFrom);
                    const validUntil = zurichDate(offerPart?.validUntil);
                    return dateFrom
                        && isEqual(validFrom, dateFrom)
                        && (!dateUntil || isEqual(validUntil, dateUntil))
                        && (!startingPoint || offerPart?.startingPoint?.name === startingPoint)
                        && (!option || offerPart?.option?.name === option);
                });

                if (offer) {
                    return transformOfferActivity(offer);
                }
                return undefined;
            },
        );
    }
    catch (error) {
        throw new Error('Failed to load final activity offer', { cause: error });
    }
}

function getFinalPassengerSpecifications(passengers: Passenger[]): ApiSchema['AnonymousPassengerSpecification'][] {
    return passengers.map(p => ({
        externalRef: p.ref,
        type: 'PERSON',
        dateOfBirth: formatDateISO(p.dateOfBirth),
        cards: p.reductionCards,
        ...(p.promotionCode && { promotionCode: { code: p.promotionCode } }),
    }));
}
